import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Loader2, RefreshCcw } from "lucide-react";
import { ArticleCard } from "@/components/ArticleCard";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { Header } from "@/components/Header";
import { FilterModal } from "@/components/FilterModal";
import { NewsSkeletonLoader } from "@/components/NewsSkeletonLoader";
import { useArticles } from "@/hooks/ainews-hooks/useArticles";
import { useInfiniteScroll } from "@/hooks/ainews-hooks/useInfiniteScroll";
import { useRateLimit } from "@/hooks/ainews-hooks/useRateLimit";
import { supabase } from "@/integrations/news-supabase-integration/supabase/client";
import { Button } from "@/components/ui/button";
import { Sparkles, Filter } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Sidebar } from "@/components/Sidebar";
import { useIsDarkMode } from "@/hooks/useIsDarkMode";
import { cn } from "@/lib/utils";
import GlobalAudioBar from "@/components/GlobalAudioBar";
import noArticlesFoundImg from "@/assets/no-articles-found.png";

interface Article {
  title: string;
  description: string;
  url: string;
  imageUrl?: string;
  publishedAt: string;
  source: {
    name: string;
    domain: string;
  };
  country: string;
  language: string;
}

const AiNews = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [generateLoading, setGenerateLoading] = useState(false);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [loadingStates, setLoadingStates] = useState<{
    [key: string]: boolean;
  }>({});
  const [filters, setFilters] = useState<any>({});
  const isDarkMode = useIsDarkMode();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [columnCount, setColumnCount] = useState(() => {
    if (typeof window === "undefined") return 1;
    if (window.innerWidth >= 1024) return 3;
    if (window.innerWidth >= 640) return 2;
    return 1;
  });
  const { toast } = useToast();
  const { canGenerate, markGenerated, timeUntilNext } = useRateLimit(1);

  const [initialLoading, setInitialLoading] = useState(true);
  const {
    articles: dbArticles,
    loading: dbLoading,
    hasMore,
    fetchArticles: refreshArticles,
    loadMoreArticles,
    updateArticleSummary,
  } = useArticles();

  // Show loader for at least 1 second on initial load
  useEffect(() => {
    if (dbLoading) {
      setInitialLoading(true);
      const timer = setTimeout(() => setInitialLoading(false), 1000);
      return () => clearTimeout(timer);
    }
    setInitialLoading(false);
  }, [dbLoading]);

  // Global hotkey: Ctrl + B => Toggle sidebar visibility
  useEffect(() => {
    const onToggleSidebarHotkey = (e: KeyboardEvent) => {
      if (e.repeat) return;
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName?.toLowerCase();
      const isTyping = tag === "input" || tag === "textarea" || (target as any)?.isContentEditable;
      if (isTyping) return;

      const isCtrlB = e.ctrlKey && (e.key === "b" || e.key === "B");
      if (!isCtrlB) return;
      e.preventDefault();
      setIsSidebarOpen((prev) => !prev);
    };

    window.addEventListener("keydown", onToggleSidebarHotkey, { capture: false });
    return () => window.removeEventListener("keydown", onToggleSidebarHotkey as any);
  }, []);

  // Convert database articles format to display format
  const convertDbToDisplayFormat = (articles: any[]) => {
    return articles.map((article) => ({
      id: article.id,
      title: article.title,
      description: article.description || "", // Show original description if AI summary doesn't exist
      url: article.url,
      imageUrl: article.image_url,
      publishedAt: article.published_at,
      source: {
        name: article.source_name,
        domain: article.source_domain || "",
      },
      country: article.country,
      language: article.language,
      aiSummary: article.ai_summary,
    }));
  };

  const generateNews = async () => {
    if (!canGenerate) {
      toast({
        title: "Rate Limited",
        description: `Please wait ${timeUntilNext} before generating news again`,
        variant: "destructive",
      });
      return;
    }

    setGenerateLoading(true);

    try {
      const response = await supabase.functions.invoke("generate-news", {
        body: {
          searchQuery: searchQuery.trim() || undefined,
        },
      });

      if (response.error) {
        throw response.error;
      }

      if (response.data?.success) {
        markGenerated();
        // Refresh articles from database
        await refreshArticles(true, filters);
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to generate news",
        variant: "destructive",
      });
    } finally {
      setGenerateLoading(false);
    }
  };

  const handleSummarize = async (article: any) => {
    if (!article.id) {
      toast({
        title: "Error",
        description: "Article ID is required for summarization",
        variant: "destructive",
      });
      return;
    }

    setLoadingStates((prev) => ({ ...prev, [article.id]: true }));

    try {
      const response = await supabase.functions.invoke("summarize-article", {
        body: {
          articleId: article.id,
          title: article.title,
          description: article.description,
          source: article.source?.name,
          publishedAt: article.publishedAt,
        },
      });

      if (response.error) {
        throw response.error;
      }

      if (response.data?.success) {
        await updateArticleSummary(article.id, response.data.summary);
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to generate summary",
        variant: "destructive",
      });
    } finally {
      setLoadingStates((prev) => ({ ...prev, [article.id]: false }));
    }
  };

  const handleSearch = () => {
    const searchFilters = { ...filters, searchQuery: searchQuery.trim() };
    setFilters(searchFilters);
    refreshArticles(true, searchFilters);
  };

  const handleApplyFilters = (newFilters: any) => {
    const combinedFilters = { ...newFilters, searchQuery: searchQuery.trim() };
    setFilters(combinedFilters);
    refreshArticles(true, combinedFilters);
  };

  // Show articles from database
  const displayArticles = convertDbToDisplayFormat(dbArticles);
  const isLoading = dbLoading;

  // Theme is derived from ThemeProvider via useIsDarkMode; no manual DOM toggling here

  useEffect(() => {
    if (typeof window === "undefined") return;

    const getColumnCount = () => {
      if (window.innerWidth >= 1024) return 3;
      if (window.innerWidth >= 640) return 2;
      return 1;
    };

    const handleResize = () => {
      setColumnCount((prev) => {
        const next = getColumnCount();
        return prev === next ? prev : next;
      });
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const articleColumns = useMemo(() => {
    const count = Math.max(columnCount, 1);
    const columns = Array.from({ length: count }, () => [] as { article: ReturnType<typeof convertDbToDisplayFormat>[number]; index: number }[]);

    displayArticles.forEach((article, index) => {
      columns[index % count].push({ article, index });
    });

    return columns;
  }, [displayArticles, columnCount]);

  // Infinite scroll setup
  const { loadMoreRef } = useInfiniteScroll({
    loading: isLoading,
    hasMore,
    onLoadMore: loadMoreArticles,
  });

  const trimmedSearchQuery = searchQuery.trim();
  const formattedSearchQuery = trimmedSearchQuery
    ? trimmedSearchQuery.charAt(0).toUpperCase() + trimmedSearchQuery.slice(1)
    : "";

  return (
    <div
      className={cn(
        "min-h-screen pt-16 transition-colors duration-300 overflow-x-hidden",
        isDarkMode
          ? "bg-[#303030] text-slate-100"
          : "bg-[#f3f4f8] text-slate-900"
      )}
    >
      {/* Sidebar */}
      <Sidebar
        isOpen={isSidebarOpen}
        onToggle={() => setIsSidebarOpen((o) => !o)}
        onNewChat={() => navigate("/")}
        isDarkMode={isDarkMode}
        onAuth={() => {}}
        user={undefined as any}
      />

      <Header
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        onSearchSubmit={handleSearch}
        onRefresh={generateNews}
        onFilterClick={() => setFilterModalOpen(true)}
        onMenuClick={() => setIsSidebarOpen(true)}
        isRefreshing={generateLoading}
        canGenerate={canGenerate}
        timeUntilNext={timeUntilNext}
      />

      <main className="container mx-auto px-2 xs:px-6 py-8 lg:py-10">
        <div
          className={cn(
            "rounded-4xl border-0 px-0 xs:px-4 pb-6 lg:pb-10 transition-colors"
          )}
        >
        {trimmedSearchQuery && (
          <div className="flex items-center justify-between gap-4 mb-0">
            <div className="flex-1">
              <h2 className="text-3xl font-bold mb-2">
                <div className="flex items-center justify-center gap-2 mt-1.5">
                  <Search className="w-6 h-6" />
                  <span>{formattedSearchQuery}</span>
                </div>
              </h2>
            </div>
          </div>
        )}

        {(generateLoading || initialLoading) && (
          <NewsSkeletonLoader isDarkMode={isDarkMode} />
        )}

        {!generateLoading &&
          !initialLoading &&
          !isLoading &&
          displayArticles.length === 0 &&
          (dbArticles.length === 0 ? (
            <div className="text-center py-12">
              <img 
                src={noArticlesFoundImg}
                alt="No articles found" 
                className="w-32 h-32 mx-auto mb-4 opacity-60"
              />
              <h3 className="text-lg font-medium mb-2">No articles found</h3>
              <p className="text-muted-foreground mb-6">
                Try adjusting your search or refreshing news articles
              </p>
            </div>
          ) : (
            <div className="text-center py-12">
              <img 
                src={noArticlesFoundImg} 
                alt="No articles found" 
                className="w-32 h-32 mx-auto mb-4 opacity-60"
              />
              <h3 className="text-lg font-semibold mb-2">No Articles Found</h3>
              <p className="text-muted-foreground mb-4">
                {searchQuery || Object.keys(filters).length > 0
                  ? "Try adjusting your search or filters"
                  : "Click 'Generate News' to fetch the latest articles"}
              </p>
              <Button
                onClick={generateNews}
                disabled={generateLoading || !canGenerate}
                className="glass"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                {!canGenerate ? `Wait ${timeUntilNext}` : "Generate News"}
              </Button>
            </div>
          ))}

        {displayArticles.length > 0 && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 px-0 pb-4 xs:p-4 pt-0 w-full">
              {articleColumns.map((column, columnIndex) => (
                <div key={`article-column-${columnIndex}`} className="flex flex-col gap-4">
                  {column.map(({ article, index }) => (
                    <ArticleCard
                      key={article.id || article.url}
                      article={article}
                      onSummarize={handleSummarize}
                      isGeneratingSummary={loadingStates[article.id || ""]}
                      isDarkMode={isDarkMode}
                      prefetchUrls={displayArticles
                        .slice(index + 1, index + 3)
                        .map((a) => a.imageUrl)
                        .filter((u): u is string => Boolean(u))}
                    />
                  ))}
                </div>
              ))}
            </div>

            {/* Infinite scroll trigger */}
            <div ref={loadMoreRef} className="py-8">
              {isLoading && (
                <div className="w-full flex flex-col items-center justify-center gap-4 py-4">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  <p className="text-sm text-muted-foreground">Loading more articles...</p>
                </div>
              )}
              {!hasMore && displayArticles.length > 0 && (
                <div className="text-center text-muted-foreground">
                  <p className="opacity-50">
                    You've reached the end of the articles
                  </p>
                </div>
              )}
            </div>
          </>
        )}

        <FilterModal
          isOpen={filterModalOpen}
          onClose={() => setFilterModalOpen(false)}
          onApplyFilters={handleApplyFilters}
          currentFilters={filters}
        />
        </div>
      </main>
      {/* Sticky bottom global audio bar */}
      <GlobalAudioBar isDarkMode={isDarkMode} />
    </div>
  );
};

export default AiNews;
